import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { listOntologyTerms } from "../../../api/content/ontologyApi";
import { useLoad } from "../../../shared/loading/useLoad";
import { appPaths } from "../../../shared/routing/appPaths";
import { useWorkspace } from "../../../shell/workspace";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import type { EntityLookup } from "../viewer/detailParts";
import { buildOntologyViewerModel } from "../viewer/ontologyModel";
import { buildOntologyEntityIndexes } from "../viewer/relationTree";
import TermDetailCard from "./TermDetailCard";

/**
 * The term card as the "Edit an item" step's view (ADR-0031 D2), for a term of the model
 * version `commit`: the same card as on the Data model, with what it reads beside it there.
 * Its Save answers the step it is shown in. A related term opens on the Data model, beside.
 */
export default function TermEditView({ projectId, commit, iri }: { projectId: string; commit: string; iri: string }) {
  const { t, i18n } = useTranslation();
  const workspace = useWorkspace();
  const load = useCallback((signal: AbortSignal) => listOntologyTerms(projectId, commit, null, signal), [projectId, commit]);
  const { state, reload } = useLoad(load);
  const listing = state.status === "loaded" ? state.value : null;
  const lookup = useMemo<EntityLookup | null>(() => {
    if (listing === null) return null;
    const indexes = buildOntologyEntityIndexes(buildOntologyViewerModel(listing, i18n.language));
    return {
      ...indexes,
      onSelectRelatedEntity: (related) => { workspace.open(appPaths.ontology({ version: commit, termIri: related }), { placement: "beside" }); },
    };
  }, [listing, i18n.language, workspace, commit]);
  if (listing === null || lookup === null) return <LoadStatus state={state} loadingLabel={t("ontologyViewer.loadingTerm")} onRetry={reload} />;
  return (
    <TermDetailCard
      projectId={projectId}
      commit={commit}
      iri={iri}
      canPropose={true}
      knownIris={listing.terms.map((term) => term.iri)}
      modules={listing.modules}
      lookup={lookup}
      editStep="shown"
    />
  );
}
